/**
 * Lieferantenverträge — Typen und Anzeige-Helfer.
 *
 * Backend: hetzner/webhook/kontor_vertraege_api.py. Der Perioden-Status wird
 * dort GERECHNET (lieferant_vertrag_service.py) und kommt fertig mit —
 * das Frontend zeigt ihn nur, es rechnet nie selbst nach.
 * Konzept: docs/KONZEPT-LIEFERANTENVERTRAEGE.md.
 */

export type PeriodeStatus =
  | "erwartet" | "fehlt" | "beleg_da" | "ohne_beleg" | "bezahlt" | "abweichung";

export interface VertragPeriode {
  id: string;
  vertrag_id: string;
  periode: string;
  faelligkeit: string | null;
  soll_brutto: number | null;
  beleg_id: string | null;
  kontobewegung_id: string | null;
  notiz: string;
  status: PeriodeStatus;
  beleg_brutto: number | null;
  beleg_datum: string | null;
  beleg_status: string | null;
  beleg_rechnungsnummer: string | null;
  bank_betrag: number | null;
  bank_datum: string | null;
}

/**
 * Vertragsunterlage — Jahresrechnung mit Ratenplan, Gutschrift beim
 * Vertragswechsel, 0-€-Rechnung. Beschreibt den Vertrag, belegt keine
 * Zahlung; steht deshalb hier statt in der Belegverarbeitung und geht nie an
 * DATEV (beleg_typ „vertragsunterlage").
 */
export interface VertragUnterlage {
  id: string;
  datum: string;
  bruttobetrag: number;
  rechnungsnummer: string | null;
  notiz: string;
  hat_datei: boolean;
}

export interface Vertrag {
  id: string;
  lieferant_firma_id: string;
  lieferant_name: string;
  bezeichnung: string;
  kennung: string;
  pruefart: "betrag" | "rhythmus";
  rhythmus: "woechentlich" | "monatlich" | "quartalsweise" | "jaehrlich";
  soll_brutto: number | null;
  zahlweise: "lastschrift" | "ueberweisung" | "karte";
  beginn: string | null;
  ende: string | null;
  kuendigungsfrist: string;
  status: "aktiv" | "beendet";
  notizen: string;
  perioden: VertragPeriode[];
  unterlagen: VertragUnterlage[];
  uebersicht: {
    ampel: "gruen" | "gelb" | "rot";
    zaehler: Partial<Record<PeriodeStatus, number>>;
    hinweis: string;
    perioden_gesamt: number;
  };
}

export interface BelegKandidat {
  id: string;
  datum: string;
  bruttobetrag: number;
  status: string;
  rechnungsnummer: string | null;
}

/** Anzeige je Perioden-Status: Text + Tonlage (steuert die Farbe im CSS). */
export const STATUS_INFO: Record<PeriodeStatus, { text: string; ton: "ok" | "warn" | "rot" | "neutral" }> = {
  erwartet:   { text: "erwartet",        ton: "neutral" },
  fehlt:      { text: "fehlt",           ton: "rot" },
  beleg_da:   { text: "Beleg da",        ton: "warn" },
  ohne_beleg: { text: "ohne Beleg",      ton: "rot" },
  bezahlt:    { text: "bezahlt",         ton: "ok" },
  abweichung: { text: "Betrag weicht ab", ton: "rot" },
};

export const RHYTHMUS_TEXT: Record<Vertrag["rhythmus"], string> = {
  woechentlich: "wöchentlich",
  monatlich: "monatlich",
  quartalsweise: "quartalsweise",
  jaehrlich: "jährlich",
};

export const ZAHLWEISE_TEXT: Record<Vertrag["zahlweise"], string> = {
  lastschrift: "Lastschrift",
  ueberweisung: "Überweisung",
  karte: "Karte",
};

/**
 * Verständliche Meldung aus einem ApiError. `err.message` ist nutzlos —
 * die echte Meldung steht in `err.body.detail`, bei Pydantic als Liste.
 */
export function fehlermeldung(err: unknown): string {
  const body = (err as { body?: { detail?: unknown } })?.body;
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d as { msg?: string })?.msg || String(d))
      .join(" · ");
  }
  return err instanceof Error && err.message ? err.message : "Unbekannter Fehler.";
}
